// wwwroot/js/webSerialBarcodeScanner.js
// Loaded via <script src="..."></script>
// All APIs are under window.jsScanner

(function () {
    "use strict";

    const API_NAME = "jsScanner";
    if (window[API_NAME]) return; // avoid double-registration

    let _dotNet = null;
    let _port = null;
    let _reader = null;
    let _keepReading = false;
    let _isClosing = false;
    let _handlingTransportLoss = false;

    // ---- Scan aggregation state (PDF417-friendly) ----
    let _scanBuffer = "";
    let _scanTimer = null;
    let _pdfLikely = false;
    let _navigatorEventsHooked = false;
    let _activeOptions = null;

    // ---- Reconnect / identity state ----
    let _manualCloseRequested = false;
    let _reconnectTimer = null;
    let _reconnectInProgress = false;
    let _currentPortLabel = null;

    // Browser-local identity for the last successfully selected scanner.
    // Web Serial does not expose COM numbers, serial numbers, or friendly names reliably.
    // VID/PID is the best stable identity available through getInfo().
    const PREF_IDENTITY = "VisitForm.WebSerialScanner.Identity.v1";

    // Default timeouts
    const DEFAULT_SCAN_IDLE_MS = 150;  // 1D barcodes
    const DEFAULT_PDF_IDLE_MS = 450;   // PDF417 multi-burst
    const DEFAULT_RECONNECT_MS = 2000;

    // Terminators some scanners append
    const TERM_EOT = "\x04"; // End of Transmission
    const TERM_ETX = "\x03"; // End of Text

    // AAMVA control chars
    const RS = "\x1E"; // Record Separator, 0x1E
    const GS = "\x1D"; // Group Separator, 0x1D

    function isSupported() {
        return typeof navigator !== "undefined" && !!navigator.serial;
    }

    function log(msg) {
        try { _dotNet && _dotNet.invokeMethodAsync("JsLog", String(msg)); } catch { }
    }

    function status(isConnected, portName, lastError) {
        try {
            _dotNet && _dotNet.invokeMethodAsync("JsStatus", {
                IsConnected: !!isConnected,
                PortName: portName ?? null,
                LastScanAt: null,
                LastError: lastError ?? null
            });
        } catch { }
    }

    // ---------------------------------------------------------------------
    // Options / identity helpers
    // ---------------------------------------------------------------------

    function normalizeUsbId(value) {
        if (value === null || value === undefined || value === "") return null;

        if (typeof value === "number" && Number.isFinite(value)) {
            const n = Math.trunc(value);
            return n >= 0 ? n : null;
        }

        const s = String(value).trim();
        if (!s) return null;

        let n;
        if (s.startsWith("0x") || s.startsWith("0X")) {
            n = parseInt(s.substring(2), 16);
        } else {
            n = parseInt(s, 10);
        }

        return Number.isFinite(n) && n >= 0 ? n : null;
    }

    function normalizeOptions(options) {
        const o = options || {};
        const parity = String(o.parity ?? "none").toLowerCase();
        const stopBits = Number(o.stopBits ?? 1);

        const scanIdleMs = Number(o.scanIdleMs ?? DEFAULT_SCAN_IDLE_MS);
        const pdf417IdleMs = Number(o.pdf417IdleMs ?? DEFAULT_PDF_IDLE_MS);
        const reconnectDelayMs = Number(o.reconnectDelayMs ?? DEFAULT_RECONNECT_MS);

        return {
            baudRate: Number(o.baudRate ?? 9600),
            dataBits: Number(o.dataBits ?? 8),
            stopBits: (stopBits === 2 ? 2 : 1),
            parity: (parity === "even" || parity === "odd") ? parity : "none",

            interChunkFlushMs: Number(o.interChunkFlushMs ?? 60),

            usbVendorId: normalizeUsbId(o.usbVendorId),
            usbProductId: normalizeUsbId(o.usbProductId),

            scanIdleMs: Number.isFinite(scanIdleMs) && scanIdleMs > 0 ? scanIdleMs : DEFAULT_SCAN_IDLE_MS,
            pdf417IdleMs: Number.isFinite(pdf417IdleMs) && pdf417IdleMs > 0 ? pdf417IdleMs : DEFAULT_PDF_IDLE_MS,

            // Auto-reconnect uses navigator.serial.getPorts(), so it only works after
            // the user has previously authorized the scanner.
            autoReconnect: o.autoReconnect !== false,
            reconnectDelayMs: Number.isFinite(reconnectDelayMs) && reconnectDelayMs > 0
                ? reconnectDelayMs
                : DEFAULT_RECONNECT_MS,

            // Safety valve:
            // If no VID/PID or saved identity exists, allow opening the only authorized
            // serial port. If multiple authorized ports exist, do not pick randomly.
            allowSingleAuthorizedFallback: o.allowSingleAuthorizedFallback !== false
        };
    }

    function loadSavedIdentity() {
        try {
            const raw = window.localStorage && window.localStorage.getItem(PREF_IDENTITY);
            if (!raw) return null;

            const parsed = JSON.parse(raw);
            const vid = normalizeUsbId(parsed.usbVendorId);
            const pid = normalizeUsbId(parsed.usbProductId);

            if (!vid) return null;

            return {
                usbVendorId: vid,
                usbProductId: pid,
                label: parsed.label ? String(parsed.label) : null,
                savedAt: parsed.savedAt ? String(parsed.savedAt) : null
            };
        } catch {
            return null;
        }
    }

    function saveIdentityFromPort(port, label) {
        try {
            const info = getPortInfoSafe(port);
            if (!info || !info.usbVendorId) return;

            const identity = {
                usbVendorId: info.usbVendorId,
                usbProductId: info.usbProductId ?? null,
                label: label ?? null,
                savedAt: new Date().toISOString()
            };

            window.localStorage && window.localStorage.setItem(PREF_IDENTITY, JSON.stringify(identity));
        } catch {
            // ignore persistence errors
        }
    }

    function clearSavedIdentity() {
        try {
            window.localStorage && window.localStorage.removeItem(PREF_IDENTITY);
        } catch {
            // ignore persistence errors
        }
    }

    function getPortInfoSafe(port) {
        try {
            return port && port.getInfo ? port.getInfo() : null;
        } catch {
            return null;
        }
    }

    function usbIdToHex(value) {
        const n = normalizeUsbId(value);
        if (n === null) return null;
        return "0x" + n.toString(16).padStart(4, "0");
    }

    // ---------------------------------------------------------------------
    // PDF417 detection / scan aggregation
    // ---------------------------------------------------------------------

    function updatePdfLikelyHeuristic(appendedText) {
        if (_pdfLikely) return;

        const combined = (_scanBuffer + appendedText);

        if (combined.startsWith("@")) { _pdfLikely = true; return; }
        if (combined.indexOf(RS) >= 0) { _pdfLikely = true; return; }
        if (combined.indexOf(GS) >= 0) { _pdfLikely = true; return; }
        if (combined.toUpperCase().indexOf("ANSI ") >= 0) { _pdfLikely = true; return; }
    }

    function clearScanTimer() {
        try { if (_scanTimer) clearTimeout(_scanTimer); } catch { }
        _scanTimer = null;
    }

    function resetScanTimer() {
        clearScanTimer();

        const o = _activeOptions || {
            scanIdleMs: DEFAULT_SCAN_IDLE_MS,
            pdf417IdleMs: DEFAULT_PDF_IDLE_MS
        };

        const ms = _pdfLikely ? o.pdf417IdleMs : o.scanIdleMs;

        _scanTimer = setTimeout(() => {
            finalizeScanIfAny("idle-timeout");
        }, ms);
    }

    function processTerminators() {
        while (true) {
            const iEot = _scanBuffer.indexOf(TERM_EOT);
            const iEtx = _scanBuffer.indexOf(TERM_ETX);

            let idx = -1;
            if (iEot >= 0 && iEtx >= 0) idx = Math.min(iEot, iEtx);
            else idx = (iEot >= 0) ? iEot : iEtx;

            if (idx < 0) break;

            const one = _scanBuffer.slice(0, idx);
            _scanBuffer = _scanBuffer.slice(idx + 1);

            emitScan(one, "terminator");
            _pdfLikely = false;
        }
    }

    // --------------------------
    // AAMVA delimiter alignment
    // --------------------------

    function looksLikeAamva(s) {
        if (!s) return false;
        return s.startsWith("@") ||
            s.indexOf(RS) >= 0 ||
            s.toUpperCase().indexOf("ANSI ") >= 0;
    }

    function normalizeAamvaDelimiters(raw) {
        if (!raw) return "";

        let s = raw;

        // Remove NULs
        s = s.replace(/\u0000/g, "");

        // Collapse CRLF to LF, but do not globally convert lone CR <-> LF.
        s = s.replace(/\r\n/g, "\n");

        if (!looksLikeAamva(s)) return s;

        // Trim junk before '@' if present.
        const at = s.indexOf("@");
        if (at > 0) s = s.slice(at);

        // Enforce: after '@' must be LF.
        if (s.length === 1) {
            s = s + "\n";
        } else if (s[1] === "\r") {
            s = "@" + "\n" + s.slice(2);
        } else if (s[1] !== "\n") {
            s = "@" + "\n" + s.slice(1);
        }

        // Enforce: after first RS must be CR.
        const rsIdx = s.indexOf(RS);
        if (rsIdx >= 0) {
            const nextIdx = rsIdx + 1;
            if (nextIdx >= s.length) {
                s = s + "\r";
            } else if (s[nextIdx] === "\n") {
                s = s.slice(0, nextIdx) + "\r" + s.slice(nextIdx + 1);
            } else if (s[nextIdx] !== "\r") {
                s = s.slice(0, nextIdx) + "\r" + s.slice(nextIdx);
            }
        }

        if (!s.endsWith("\r")) s = s + "\r";

        return s;
    }

    function sanitizeScan(scan) {
        if (!scan) return "";

        // Do not globally convert CR <-> LF.
        let s = normalizeAamvaDelimiters(scan);

        if (!looksLikeAamva(s)) {
            s = s.replace(/\r\n/g, "\n");
        }

        // Preserve internal control delimiters. Only remove edge line breaks.
        s = s.replace(/^[\r\n]+/, "").replace(/[\r\n]+$/, "");

        return s;
    }

    function emitScan(scan, reason) {
        const payload = sanitizeScan(scan);
        if (!payload) return;

        try {
            _dotNet && _dotNet.invokeMethodAsync("JsBarcodeScanned", payload);
        } catch { }
    }

    function finalizeScanIfAny(reason) {
        clearScanTimer();

        if (!_scanBuffer) return;

        processTerminators();

        if (_scanBuffer) {
            const remaining = _scanBuffer;
            _scanBuffer = "";
            emitScan(remaining, reason);
            _pdfLikely = false;
        }
    }

    function onTextReceived(text) {
        if (!text) return;

        updatePdfLikelyHeuristic(text);
        _scanBuffer += text;

        processTerminators();
        resetScanTimer();
    }

    // ---------------------------------------------------------------------
    // Reconnect handling
    // ---------------------------------------------------------------------

    function clearReconnectTimer() {
        try { if (_reconnectTimer) clearTimeout(_reconnectTimer); } catch { }
        _reconnectTimer = null;
    }

    function scheduleReconnect(reason) {
        clearReconnectTimer();

        if (_manualCloseRequested) return;
        if (!_activeOptions || _activeOptions.autoReconnect === false) return;
        if (!isSupported()) return;
        if (_port && _port.readable) return;

        const ms = _activeOptions.reconnectDelayMs || DEFAULT_RECONNECT_MS;

        log(`Scheduling serial reconnect in ${ms} ms${reason ? ": " + reason : ""}.`);

        _reconnectTimer = setTimeout(async () => {
            _reconnectTimer = null;

            if (_manualCloseRequested) return;
            if (_reconnectInProgress) return;
            if (_port && _port.readable) return;

            _reconnectInProgress = true;
            try {
                const ok = await tryOpenAuthorized(_activeOptions, true);
                if (!ok && !_manualCloseRequested) {
                    scheduleReconnect("authorized port not available yet");
                }
            } finally {
                _reconnectInProgress = false;
            }
        }, ms);
    }

    async function handleTransportLoss(reason) {
        if (_handlingTransportLoss) return;

        _handlingTransportLoss = true;

        try {
            log(reason || "Serial transport lost.");
            await internalClose(true, {
                clearReconnect: false,
                manual: false
            });

            status(false, null, reason || "Disconnected");
            scheduleReconnect(reason || "disconnected");
        } finally {
            _handlingTransportLoss = false;
        }
    }

    // ---------------------------------------------------------------------
    // Serial reading
    // ---------------------------------------------------------------------

    async function startReadLoop() {
        if (!_port || !_port.readable) {
            log("Port is not readable.");
            status(false, null, "Port is not readable.");
            scheduleReconnect("port not readable");
            return;
        }

        _keepReading = true;

        if (typeof TextDecoderStream !== "undefined") {
            const decoder = new TextDecoderStream();
            const pipeClosed = _port.readable.pipeTo(decoder.writable).catch(() => { });

            _reader = decoder.readable.getReader();

            try {
                while (_keepReading) {
                    const { value, done } = await _reader.read();
                    if (done) break;
                    if (value) onTextReceived(value);
                }
            } catch (e) {
                if (_keepReading && !_isClosing) {
                    log("Read loop error: " + getErrorMessage(e));
                }
            } finally {
                try { if (_reader) _reader.releaseLock(); } catch { }
                _reader = null;

                try { await pipeClosed; } catch { }

                if (_keepReading && !_isClosing) {
                    await handleTransportLoss("Serial read loop ended.");
                }
            }

            return;
        }

        // Fallback: decode Uint8Array manually
        const td = new TextDecoder();
        _reader = _port.readable.getReader();

        try {
            while (_keepReading) {
                const { value, done } = await _reader.read();
                if (done) break;
                if (value) onTextReceived(td.decode(value, { stream: true }));
            }
        } catch (e) {
            if (_keepReading && !_isClosing) {
                log("Read loop error: " + getErrorMessage(e));
            }
        } finally {
            try { td.decode(); } catch { }
            try { if (_reader) _reader.releaseLock(); } catch { }
            _reader = null;

            if (_keepReading && !_isClosing) {
                await handleTransportLoss("Serial read loop ended.");
            }
        }
    }

    // ---------------------------------------------------------------------
    // Port open/close / selection
    // ---------------------------------------------------------------------

    function buildFilters(options) {
        const filters = [];

        if (options && options.usbVendorId) {
            const f = { usbVendorId: options.usbVendorId };
            if (options.usbProductId) f.usbProductId = options.usbProductId;
            filters.push(f);
        }

        return filters.length ? filters : null;
    }

    async function getPortLabel(port) {
        try {
            const info = getPortInfoSafe(port);
            if (info && info.usbVendorId) {
                const vid = usbIdToHex(info.usbVendorId);
                const pid = info.usbProductId
                    ? usbIdToHex(info.usbProductId)
                    : "unknown";

                return `USB VID:${vid} PID:${pid}`;
            }
        } catch { }

        return "SerialPort";
    }

    function scoreAuthorizedPort(port, options, savedIdentity, totalPortCount) {
        const info = getPortInfoSafe(port);
        let score = 0;

        const hasWantedVid = !!options.usbVendorId;
        const hasWantedPid = !!options.usbProductId;

        if (hasWantedVid) {
            if (!info || info.usbVendorId !== options.usbVendorId) {
                return -100000;
            }

            score += 120;

            if (hasWantedPid) {
                if (info.usbProductId !== options.usbProductId) {
                    return -100000;
                }

                score += 40;
            }
        }

        if (savedIdentity && savedIdentity.usbVendorId && info && info.usbVendorId === savedIdentity.usbVendorId) {
            score += 90;

            if (savedIdentity.usbProductId && info.usbProductId === savedIdentity.usbProductId) {
                score += 40;
            }
        }

        // If no identity info exists and only one authorized port exists,
        // keep legacy behavior but avoid random selection when there are multiple.
        if (score === 0 &&
            !hasWantedVid &&
            !savedIdentity &&
            options.allowSingleAuthorizedFallback &&
            totalPortCount === 1) {
            score += 10;
        }

        return score;
    }

    function chooseAuthorizedPort(ports, options) {
        if (!ports || ports.length === 0) return null;

        const savedIdentity = loadSavedIdentity();

        const ranked = ports
            .map(p => ({
                port: p,
                score: scoreAuthorizedPort(p, options, savedIdentity, ports.length)
            }))
            .filter(x => x.score > 0)
            .sort((a, b) => b.score - a.score);

        if (ranked.length === 0) return null;

        return ranked[0].port;
    }

    async function internalClose(emit = true, closeOptions = {}) {
        const clearReconnect = closeOptions.clearReconnect !== false;
        const manual = closeOptions.manual === true;

        if (manual) {
            _manualCloseRequested = true;
        }

        if (clearReconnect) {
            clearReconnectTimer();
        }

        _isClosing = true;
        _keepReading = false;

        try {
            finalizeScanIfAny("close");
        } catch {
            // ignore scan finalization errors
        }

        try { if (_reader) await _reader.cancel(); } catch { }
        try { if (_reader) _reader.releaseLock(); } catch { }
        _reader = null;

        try {
            if (_port) {
                await _port.close();
            }
        } catch {
            // close can fail after a physical disconnect; ignore
        }

        _port = null;
        _currentPortLabel = null;

        _scanBuffer = "";
        _pdfLikely = false;

        _isClosing = false;

        if (emit) {
            status(false, null, null);
            log("Port closed.");
        }
    }

    async function openPort(port, options) {
        const o = normalizeOptions(options);
        _activeOptions = o;
        _manualCloseRequested = false;

        clearReconnectTimer();

        if (_port && _port === port && _port.readable) {
            const label = await getPortLabel(_port);
            _currentPortLabel = label;
            status(true, label, null);
            log("Port already open (same port).");
            return true;
        }

        if (_port && _port !== port) {
            log("Different port already open. Closing previous port...");
            await internalClose(false, {
                clearReconnect: false,
                manual: false
            });
        }

        try {
            await port.open({
                baudRate: o.baudRate,
                dataBits: o.dataBits,
                stopBits: o.stopBits,
                parity: o.parity
            });
        } catch (e) {
            const msg = getErrorMessage(e);
            const name = e && e.name ? e.name : "";

            if (name === "InvalidStateError" || msg.toLowerCase().includes("already open")) {
                log("Port open requested but it is already open. Continuing.");
            } else {
                try { await port.close(); } catch { }
                status(false, null, msg);
                log("Failed to open port: " + msg);
                throw e;
            }
        }

        _port = port;

        try {
            _port.addEventListener("disconnect", async () => {
                if (_isClosing || _manualCloseRequested) return;

                log("Port disconnected.");
                await handleTransportLoss("Disconnected");
            }, { once: true });
        } catch {
            // not fatal
        }

        const label = await getPortLabel(_port);
        _currentPortLabel = label;
        saveIdentityFromPort(_port, label);

        status(true, label, null);
        log("Port opened/active: " + label);

        if (!_keepReading) {
            startReadLoop();
        }

        return true;
    }

    function getErrorMessage(e) {
        return e && e.message ? e.message : String(e);
    }

    // ---------------------------------------------------------------------
    // Public API
    // ---------------------------------------------------------------------

    async function init(dotNetRef) {
        _dotNet = dotNetRef;

        if (!isSupported()) {
            status(false, null, "Web Serial not supported in this browser.");
            return false;
        }

        if (!_navigatorEventsHooked) {
            _navigatorEventsHooked = true;

            try {
                navigator.serial.addEventListener("connect", () => {
                    log("Serial device connected.");
                    scheduleReconnect("serial device connected");
                });

                navigator.serial.addEventListener("disconnect", () => {
                    log("Serial device disconnected.");
                });
            } catch {
                // ignore event hook errors
            }
        }

        status(false, null, null);
        return true;
    }

    async function tryOpenAuthorized(options, internalReconnectAttempt) {
        if (!isSupported()) return false;

        const o = normalizeOptions(options);
        _activeOptions = o;

        let ports;
        try {
            ports = await navigator.serial.getPorts();
        } catch (e) {
            const msg = getErrorMessage(e);
            status(false, null, msg);
            log("Failed to enumerate authorized ports: " + msg);
            return false;
        }

        if (!ports || ports.length === 0) {
            if (!internalReconnectAttempt) {
                log("No previously authorized ports.");
            }
            return false;
        }

        const chosen = chooseAuthorizedPort(ports, o);

        if (!chosen) {
            const saved = loadSavedIdentity();
            const savedText = saved
                ? ` Saved identity VID:${usbIdToHex(saved.usbVendorId)} PID:${saved.usbProductId ? usbIdToHex(saved.usbProductId) : "unknown"}.`
                : "";

            const wantedText = o.usbVendorId
                ? ` Wanted VID:${usbIdToHex(o.usbVendorId)} PID:${o.usbProductId ? usbIdToHex(o.usbProductId) : "any"}.`
                : "";

            const msg = `No matching authorized serial scanner found.${wantedText}${savedText}`;
            if (!internalReconnectAttempt) {
                status(false, null, msg);
                log(msg);
            }

            return false;
        }

        try {
            await openPort(chosen, o);
            return true;
        } catch (e) {
            const msg = getErrorMessage(e);
            status(false, null, msg);
            log("Failed to open authorized port: " + msg);

            if (internalReconnectAttempt) {
                scheduleReconnect("open failed");
            }

            return false;
        }
    }

    async function requestAndOpen(options) {
        if (!isSupported()) throw new Error("Web Serial not supported.");

        const o = normalizeOptions(options);
        _activeOptions = o;
        _manualCloseRequested = false;
        clearReconnectTimer();

        if (_port && _port.readable) {
            const label = await getPortLabel(_port);
            _currentPortLabel = label;
            status(true, label, null);
            log("Connect requested but port is already open.");
            return true;
        }

        const filters = buildFilters(o);

        const port = filters
            ? await navigator.serial.requestPort({ filters })
            : await navigator.serial.requestPort();

        return await openPort(port, o);
    }

    async function close() {
        await internalClose(true, {
            clearReconnect: true,
            manual: true
        });
    }

    async function flushNow() {
        finalizeScanIfAny("flushNow");
    }

    function forgetSavedScanner() {
        clearSavedIdentity();
        log("Saved Web Serial scanner identity cleared.");
    }

    async function getSavedScannerIdentity() {
        return loadSavedIdentity();
    }

    window[API_NAME] = {
        init,
        tryOpenAuthorized,
        requestAndOpen,
        close,
        isSupported,
        flushNow,

        // Optional helpers. Existing callers do not need to use these.
        forgetSavedScanner,
        getSavedScannerIdentity
    };
})();